import { logEvent } from '@amplitude/analytics-browser'
import {
  AppointmentBookAvailabilityShowEventProps,
  AppointmentBookAvailabilityClickEventProps,
  AppointmentBookSlotSelectedEventProps,
  AppointmentBookStartEventProps,
  AppointmentBookStateSelectedEventProps,
  AppointmentJoinStartEventProps,
  AppointmentModifyEventProps,
  AppointmentNoteOpenEventProps,
  DashboardActionClickEventProps,
  LabResultDownloadDoneEventProps,
  MessageAttachStartEventProps,
  MessageSendDoneEventProps,
  MessageThreadOpenEventProps,
  NewOrExistingAppointmentEventProps,
  AppointmentCancelModalClickEventProps,
  CommunicationsPreferencesUpdateDoneProps,
  AppointmentCancelReasonModalClickEventProps,
  CarePlanCheckInStartEventProps,
  CarePlanCheckInDoneEventProps,
  LabScheduleSlotSelectedProps,
  LabCancelDoneProps,
  LabCancelStartProps,
  LabModifyStartProps,
  LabScheduleAvailabilityShowProps,
  LabScheduleDoneProps,
  LabScheduleProviderSelectedProps,
  LabScheduleStartProps,
  LabScheduleZipSelectedProps,
  LabScheduleAvailabilityMoreTimesClickProps,
  LabAppointmentDetailsClickProps,
  DashboardActionClickProps,
} from './amplitude.types'

export class Amplitude {
  static userHasInsurance(cta: 'yes' | 'no', variant_label?: string) {
    logEvent('User Has Insurance', {
      cta,
      variant_label: variant_label ?? '',
    })
  }

  static insuranceEligibility(
    state_field: string,
    insurance_company: string,
    variant_label?: string
  ) {
    logEvent('Insurance Eligibility', {
      state_field,
      insurance_company,
      variant_label: variant_label ?? '',
    })
  }

  static insurancePlanEligibility(cta: 'yes' | 'no', variant_label?: string) {
    logEvent('User has non-commerce insurance', {
      cta,
      variant_label: variant_label ?? '',
    })
  }

  static insuranceCoverageLoaded(
    coverage: 'covered' | 'testing-covered' | 'more-info',
    variant_label?: string
  ) {
    logEvent('Insurance coverage Loaded', {
      coverage,
      variant_label: variant_label ?? '',
    })
  }

  static insuranceCoverage(
    cta: 'covered' | 'testing-covered',
    variant_label?: string
  ) {
    logEvent('Insurance coverage', {
      cta,
      variant_label: variant_label ?? '',
    })
  }

  static userSelectsProduct(cta: string, variant_label?: string) {
    logEvent('User Selects Product', {
      cta,
      variant_label: variant_label ?? '',
    })
  }

  static userViewsPaymentPlans(variant_label?: string) {
    logEvent('User Views Payment Plans', {
      variant_label: variant_label ?? '',
    })
  }

  static initiatesCheckout(
    cta: 'checkout' | 'consult',
    product_tier?: string,
    variant_label?: string
  ) {
    logEvent(
      'Initiates Checkout',
      product_tier
        ? {
            product_tier,
            cta,
            variant_label: variant_label ?? '',
          }
        : { cta, variant_label: variant_label ?? '' }
    )
  }

  static userEntersPayment(
    time_on_page_in_seconds: number,
    variant_label?: string
  ) {
    logEvent('User Enters Payment', {
      time_on_page_in_seconds,
      variant_label: variant_label ?? '',
    })
  }

  static howDidYouHear(
    choices: string[],
    cta: 'share' | 'skip',
    variant_label?: string
  ) {
    logEvent('How Did You Hear', {
      choices,
      cta,
      variant_label: variant_label ?? '',
    })
  }

  static consentToTelehealth(variant_label?: string) {
    logEvent('Consent To Telehealth', {
      variant_label: variant_label ?? '',
    })
  }

  static insuranceDetails(
    time_on_page_in_seconds: number,
    variant_label?: string
  ) {
    logEvent('Insurance Details', {
      cta: 'submit',
      time_on_page_in_seconds,
      variant_label: variant_label ?? '',
    })
  }

  static bookFirstAppointment(variant_label?: string) {
    logEvent('Book Appointment', {
      variant_label: variant_label ?? '',
    })
  }

  static loginOption(
    is_onboarding: boolean,
    choice_required?: 'email_or_phone' | 'otp_or_password',
    option_selected?:
      | 'otp_email'
      | 'otp_phone'
      | 'password'
      | 'email'
      | 'phone'
      | 'otp'
  ) {
    logEvent('LoginOptionSelected', {
      choice_required: choice_required ?? '',
      is_onboarding,
      option_selected: option_selected ?? '',
    })
  }

  static userLogsIn(loginOption: 'otp_email' | 'otp_phone' | 'password') {
    logEvent('User Logs In', {
      loginOption,
    })
  }

  static loginErrorShow(error: string) {
    logEvent('LoginErrorShow', {
      error,
    })
  }

  static appointmentBookStart(props: AppointmentBookStartEventProps) {
    logEvent('AppointmentBookStart', props)
  }

  static appointmentBookStateSelected(
    props: AppointmentBookStateSelectedEventProps
  ) {
    logEvent('AppointmentBookStateSelected', props)
  }

  static appointmentBookAvailabilityShow(
    props: AppointmentBookAvailabilityShowEventProps
  ) {
    logEvent('AppointmentBookAvailabilityShow', props)
  }

  static appointmentBookAvailabilityClick(
    props: AppointmentBookAvailabilityClickEventProps
  ) {
    logEvent('AppointmentBookAvailabilityClick', props)
  }

  static appointmentBookSlotSelected(
    props: AppointmentBookSlotSelectedEventProps
  ) {
    logEvent('AppointmentBookSlotSelected', props)
  }

  static appointmentBookDone(props: NewOrExistingAppointmentEventProps) {
    logEvent('AppointmentBookDone', props)
  }

  static appointmentModifyStart(props: AppointmentModifyEventProps) {
    logEvent('AppointmentModifyStart', props)
  }

  static appointmentCancelStart(props: AppointmentModifyEventProps) {
    logEvent('AppointmentCancelStart', props)
  }

  static appointmentCancelPolicyClick(props: AppointmentModifyEventProps) {
    logEvent('AppointmentCancelPolicyClick', props)
  }

  static appointmentCancelModalClick(
    props: AppointmentCancelModalClickEventProps
  ) {
    logEvent('AppointmentCancelModalClick', props)
  }

  static appointmentCancelReasonModalClick(
    props: AppointmentCancelReasonModalClickEventProps
  ) {
    logEvent('AppointmentCancelReasonModalClick', props)
  }

  static appointmentCancelDone(props: AppointmentModifyEventProps) {
    logEvent('AppointmentCancelDone', props)
  }

  static appointmentJoinStart(props: AppointmentJoinStartEventProps) {
    logEvent('AppointmentJoinStart', props)
  }

  static menuDashboardClick() {
    logEvent('MenuDashboardClick')
  }
  static menuMyHealthClick() {
    logEvent('MenuMyHealthClick')
  }

  static menuMessagingClick() {
    logEvent('MenuMessagingClick')
  }

  static menuBlogClick() {
    logEvent('MenuBlogClick')
  }

  static menuPerksClick() {
    logEvent('MenuPerksClick')
  }

  static menuAccountClick() {
    logEvent('MenuAccountClick')
  }

  static menuLogOutClick() {
    logEvent('MenuLogOutClick')
  }

  static messageNewClick() {
    logEvent('MessageNewClick')
  }

  static messageThreadOpen(props: MessageThreadOpenEventProps) {
    logEvent('MessageThreadOpen', props)
  }

  static messageSendDone(props: MessageSendDoneEventProps) {
    logEvent('MessageSendDone', props)
  }

  static messageAttachStart(props: MessageAttachStartEventProps) {
    logEvent('MessageAttachStart', props)
  }

  static appointmentNoteOpen(props: AppointmentNoteOpenEventProps) {
    logEvent('AppointmentNoteOpen', props)
  }

  static labResultDownloadDone(props: LabResultDownloadDoneEventProps) {
    logEvent('LabResultDownloadDone', props)
  }

  static insuranceDownloadDone() {
    logEvent('InsuranceDownloadDone')
  }

  static insuranceUploadStart() {
    logEvent('InsuranceUploadStart')
  }

  static insuranceUploadDone() {
    logEvent('InsuranceUploadDone')
  }

  static dashboardActionClick(props: DashboardActionClickEventProps) {
    logEvent('DashboardActionClick', props)
  }

  static checkoutTestimonialClick(variant_label?: string) {
    logEvent('CheckoutTestimonialClick', {
      variant_label: variant_label ?? '',
    })
  }

  static carePlanCheckInStart(props: CarePlanCheckInStartEventProps) {
    logEvent('CarePlanCheckInStart', props)
  }

  static carePlanCheckInDone(props: CarePlanCheckInDoneEventProps) {
    logEvent('CarePlanCheckInDone', props)
  }
  // Lab Scheduling

  static labAppointmentDetailsClick(props: LabAppointmentDetailsClickProps) {
    logEvent('LabAppointmentDetailsClick', props)
  }
  static labDashboardActionClick(props: DashboardActionClickProps) {
    logEvent('LabDashboardActionClick', props)
  }
  static labScheduleStart(props: LabScheduleStartProps) {
    logEvent('LabScheduleStart', props)
  }
  static labScheduleProviderSelected(props: LabScheduleProviderSelectedProps) {
    logEvent('LabScheduleProviderSelected', props)
  }
  static labScheduleZipSelected(props: LabScheduleZipSelectedProps) {
    logEvent('LabScheduleZipSelected', props)
  }
  static labScheduleAvailabilityShow(props: LabScheduleAvailabilityShowProps) {
    logEvent('LabScheduleAvailabilityShow', props)
  }
  static labScheduleAvailabilityMoreTimesClick(
    props: LabScheduleAvailabilityMoreTimesClickProps
  ) {
    logEvent('LabScheduleAvailabilityMoreTimesClick', props)
  }
  static labScheduleSlotSelected(props: LabScheduleSlotSelectedProps) {
    logEvent('LabScheduleSlotSelected', props)
  }
  static labScheduleDone(props: LabScheduleDoneProps) {
    logEvent('LabScheduleDone', props)
  }
  static labModifyStart(props: LabModifyStartProps) {
    logEvent('LabModifyStart', props)
  }
  static labCancelStart(props: LabCancelStartProps) {
    logEvent('LabCancelStart', props)
  }
  static labCancelDone(props: LabCancelDoneProps) {
    logEvent('LabCancelDone', props)
  }

  //

  /**
   * Tracks when a user updates their email notification preferences in their user account area.
   * This specifically tracks changes to the "receives..." flags in the drip/nudge email settings
   * (receivesMedicalAppointmentEmails, receivesNutritionAppointmentEmails, receivesCareJourneyEmails)
   * that control which types of reminder and care journey emails the user receives.
   * @param props Object containing both current and previous preference states
   */
  static communicationsPreferencesUpdateDone(
    props: CommunicationsPreferencesUpdateDoneProps
  ) {
    logEvent('CommunicationsPreferencesUpdateDone', props)
  }
}
